import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import {
  CartLinesUpdateEvent,
  CartErrorEvent,
  ProductSelectEvent,
  StandardEvents,
} from '@shopify/events';

// Error message display duration
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * Add to Cart Button Component
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[]} */
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles Add to Cart button click.
   *
   * IMPORTANT:
   * This only handles the visual animation.
   * The actual add-to-cart request is handled by ProductFormComponent.
   *
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) {
      return;
    }

    const productForm =
      /** @type {ProductFormComponent | null} */ (
        this.closest('product-form-component')
      );

    const quantitySelector = productForm?.refs.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled =
        this.dataset.addToCartAnimation === 'true';

      if (
        animationEnabled &&
        !event.target.closest('.quick-add-modal')
      ) {
        this.#animateFlyToCart();
      }

      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) {
      return;
    }

    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) {
      return;
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement('fly-to-cart')
      );

    const flyToCartClass = addToCartButton.classList.contains(
      'quick-add__button'
    )
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.style.setProperty(
      '--start-opacity',
      '0'
    );

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart = async () => {
    const { addToCartButton } = this.refs;

    this.#resetTimeouts.forEach((timeoutId) =>
      clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    addToCartButton.dataset.added = 'true';

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index =
        this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * Product Form Component
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const target = this.closest(
      '.shopify-section, dialog, product-card'
    );

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#onProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    )?.value;
  }

  /**
   * Get current cart.
   */
  async #refreshCart() {
    const cartItemsComponent =
      /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */ (
        document.querySelector('cart-items-component')
      );

    if (cartItemsComponent) {
      await customElements.whenDefined(
        'cart-items-component'
      );

      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(
      `${Theme.routes.cart_url}.json`,
      {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cart: ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * Update quantity information.
   *
   * @param {{items: Array<{variant_id: number, quantity: number}>}} cart
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();

    if (!variantIdInput) {
      return;
    }

    const cartItem = cart.items.find(
      (item) =>
        item.variant_id.toString() ===
        variantIdInput.toString()
    );

    const cartQty = cartItem ? cartItem.quantity : 0;

    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Listen for cart changes.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) {
      return;
    }

    event.promise
      ?.then(({ detail }) => {
        if (
          detail?.sourceId === this.id ||
          detail?.source === 'product-form-component'
        ) {
          return;
        }

        if (detail?.items) {
          this.#updateCartQuantity({
            items: detail.items,
          });
        } else {
          this.#refreshCart().then((cart) => {
            this.#updateCartQuantity(cart);
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[product-form] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Submit handler.
   *
   * @param {Event} event
   */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId =
        this.#getIntendedVariantId();

      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({
          variantId: intendedVariantId,
          quantity,
        });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();

      return;
    }

    this.#processAddToCart(
      undefined,
      undefined,
      event
    );
  }

  #getIntendedVariantId() {
    return (
      new URL(window.location.href).searchParams.get(
        'variant'
      ) ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  #getQuantity() {
    return (
      Number(
        this.refs.quantitySelector?.getValue?.()
      ) ||
      Number(this.dataset.quantityDefault) ||
      1
    );
  }

  /**
   * MAIN ADD TO CART FUNCTION
   *
   * This is the important fixed section.
   *
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(
    overrideVariantId,
    overrideQuantity,
    event
  ) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    if (!overrideVariantId) {
      const anyButtonDisabled =
        Array.from(allAddToCartContainers).some(
          (container) =>
            container.refs.addToCartButton?.disabled
        );

      if (anyButtonDisabled) {
        return;
      }
    }

    const form = this.querySelector('form');

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }

    /*
     * Quantity validation
     */
    if (
      !overrideVariantId &&
      this.refs.quantitySelector?.canAddToCart
    ) {
      const validation =
        this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate =
          this.dataset.quantityErrorMax || '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity?.toString() || ''
          );

        if (addToCartTextError) {
          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            addToCartTextError.appendChild(
              document.createTextNode(errorMessage)
            );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    /*
     * Build form data
     */
    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set(
        'id',
        overrideVariantId
      );
    }

    if (overrideQuantity !== undefined) {
      formData.set(
        'quantity',
        overrideQuantity.toString()
      );
    }

    /*
     * Make sure Shopify knows which sections
     * need to be returned.
     */
    const sectionIds = [];

    document
      .querySelectorAll('cart-items-component')
      .forEach((item) => {
        if (
          item instanceof HTMLElement &&
          item.dataset.sectionId
        ) {
          sectionIds.push(
            item.dataset.sectionId
          );
        }
      });

    if (sectionIds.length > 0) {
      formData.set(
        'sections',
        sectionIds.join(',')
      );
    }

    const variantId = formData.get('id');

    const itemCount =
      Number(formData.get('quantity')) ||
      Number(this.dataset.quantityDefault) ||
      1;

    if (!variantId) {
      throw new Error(
        'Variant ID is required'
      );
    }

    /*
     * Create the cart event promise.
     *
     * IMPORTANT:
     * We DO NOT resolve it before the Shopify request succeeds.
     */
    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    /*
     * Send request to Shopify first.
     */
    let response;

    try {
      const fetchCfg = fetchConfig(
        'javascript',
        {
          body: formData,
        }
      );

      response = await fetch(
        Theme.routes.cart_add_url,
        {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,

            // IMPORTANT FIX:
            // Shopify /cart/add.js returns JSON.
            Accept: 'application/json',
          },
        }
      );
    } catch (error) {
      console.error(
        '[product-form] Add to cart network error:',
        error
      );

      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      return;
    }

    let responseData;

    try {
      responseData = await response.json();
    } catch (error) {
      console.error(
        '[product-form] Invalid Shopify response:',
        error
      );

      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            'Invalid response received from Shopify',
          code: 'INVALID',
        })
      );

      return;
    }

    /*
     * Shopify returned an error.
     */
    if (!response.ok || responseData.status) {
      const errorMessage =
        responseData.message ||
        responseData.description ||
        'Add to cart failed';

      console.error(
        '[product-form] Shopify add-to-cart error:',
        responseData
      );

      this.dispatchEvent(
        new CartErrorEvent({
          error: errorMessage,
          code: 'INVALID',
          detail: {
            description:
              responseData.description,
            errors:
              responseData.errors,
          },
        })
      );

      /*
       * Still resolve the event with the real cart state.
       * This prevents the cart event from remaining pending.
       */
      try {
        const ajaxCart =
          await this.#refreshCart();

        deferredEventPromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              ajaxCart
            ),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source:
              'product-form-component',
            sourceId: this.id.toString(),
            itemCount,
            productId:
              this.dataset.productId,
          },
        });
      } catch (cartError) {
        deferredEventPromise.reject(
          cartError
        );
      }

      /*
       * Display error.
       */
      if (addToCartTextError) {
        addToCartTextError.classList.remove(
          'hidden'
        );

        const textNode =
          addToCartTextError.childNodes[2];

        if (textNode) {
          textNode.textContent =
            errorMessage;
        } else {
          addToCartTextError.appendChild(
            document.createTextNode(
              errorMessage
            )
          );
        }

        this.#setLiveRegionText(
          errorMessage
        );

        this.#timeout = setTimeout(() => {
          addToCartTextError.classList.add(
            'hidden'
          );

          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }

      return;
    }

    /*
     * SUCCESS
     *
     * Shopify accepted the product.
     */
    if (addToCartTextError) {
      addToCartTextError.classList.add(
        'hidden'
      );

      addToCartTextError.removeAttribute(
        'aria-live'
      );
    }

    /*
     * Update cart from Shopify.
     */
    let cart;

    try {
      cart = await this.#refreshCart();
    } catch (error) {
      console.error(
        '[product-form] Failed to refresh cart:',
        error
      );

      deferredEventPromise.reject(error);

      return;
    }

    /*
     * Update quantity UI.
     */
    this.#updateCartQuantity(cart);

    /*
     * Resolve CartLinesUpdateEvent ONLY AFTER
     * the add-to-cart request succeeded.
     *
     * This is what cart-drawer.js listens for.
     */
    deferredEventPromise.resolve({
      cart:
        CartLinesUpdateEvent.createCartFromAjaxResponse(
          cart
        ),
      detail: {
        items: cart.items,
        source:
          'product-form-component',
        sourceId: this.id.toString(),
        itemCount,
        productId:
          this.dataset.productId,
        sections:
          responseData.sections,
        didError: false,
      },
    });

    /*
     * Accessibility announcement.
     */
    const anyAddToCartButton =
      allAddToCartContainers[0]
        ?.refs.addToCartButton;

    if (anyAddToCartButton) {
      const addedTextElement =
        anyAddToCartButton.querySelector(
          '.add-to-cart-text--added'
        );

      const addedText =
        addedTextElement?.textContent?.trim() ||
        Theme.translations.added;

      this.#setLiveRegionText(
        addedText
      );

      setTimeout(() => {
        this.#clearLiveRegionText();
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    }

    /*
     * Performance measurement.
     */
    if (event) {
      cartPerformance.measureFromEvent(
        'add:user-action',
        event
      );
    }
  }

  /**
   * Batch Add to Cart.
   *
   * Used when the user clicks Add to Cart while
   * a variant selection is still being processed.
   *
   * @param {Array<{variantId: string, quantity: number}>} items
   */
  async #processBatchAddToCart(items) {
    if (!items.length) {
      return;
    }

    const { addToCartTextError } =
      this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const sectionIds = [];

    document
      .querySelectorAll('cart-items-component')
      .forEach((item) => {
        if (
          item instanceof HTMLElement &&
          item.dataset.sectionId
        ) {
          sectionIds.push(
            item.dataset.sectionId
          );
        }
      });

    const totalQuantity =
      items.reduce(
        (sum, item) =>
          sum + item.quantity,
        0
      );

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: sectionIds.join(','),
    };

    let response;

    try {
      response = await fetch(
        Theme.routes.cart_add_url,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
    } catch (error) {
      console.error(
        '[product-form] Batch add network error:',
        error
      );

      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      return;
    }

    let responseData;

    try {
      responseData =
        await response.json();
    } catch (error) {
      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            'Invalid response received from Shopify',
          code: 'INVALID',
        })
      );

      return;
    }

    /*
     * Error
     */
    if (
      !response.ok ||
      responseData.status
    ) {
      const errorMessage =
        responseData.message ||
        responseData.description ||
        'Add to cart failed';

      this.dispatchEvent(
        new CartErrorEvent({
          error: errorMessage,
          code: 'INVALID',
          detail: {
            description:
              responseData.description,
            errors:
              responseData.errors,
          },
        })
      );

      try {
        const cart =
          await this.#refreshCart();

        deferredEventPromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              cart
            ),
          detail: {
            items: cart.items,
            source:
              'product-form-component',
            sourceId: this.id.toString(),
            itemCount:
              totalQuantity,
            productId:
              this.dataset.productId,
            didError: true,
          },
        });
      } catch (error) {
        deferredEventPromise.reject(
          error
        );
      }

      if (addToCartTextError) {
        addToCartTextError.classList.remove(
          'hidden'
        );

        const textNode =
          addToCartTextError.childNodes[2];

        if (textNode) {
          textNode.textContent =
            errorMessage;
        } else {
          addToCartTextError.appendChild(
            document.createTextNode(
              errorMessage
            )
          );
        }

        this.#setLiveRegionText(
          errorMessage
        );

        this.#timeout = setTimeout(() => {
          addToCartTextError.classList.add(
            'hidden'
          );

          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }

      return;
    }

    /*
     * SUCCESS
     */
    if (addToCartTextError) {
      addToCartTextError.classList.add(
        'hidden'
      );

      addToCartTextError.removeAttribute(
        'aria-live'
      );
    }

    let cart;

    try {
      cart =
        await this.#refreshCart();
    } catch (error) {
      deferredEventPromise.reject(
        error
      );

      return;
    }

    deferredEventPromise.resolve({
      cart:
        CartLinesUpdateEvent.createCartFromAjaxResponse(
          cart
        ),
      detail: {
        items: cart.items,
        source:
          'product-form-component',
        sourceId: this.id.toString(),
        itemCount:
          totalQuantity,
        productId:
          this.dataset.productId,
        sections:
          responseData.sections,
        didError: false,
      },
    });

    this.#updateCartQuantity(cart);

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    const anyAddToCartButton =
      allAddToCartContainers[0]
        ?.refs.addToCartButton;

    if (anyAddToCartButton) {
      const addedTextElement =
        anyAddToCartButton.querySelector(
          '.add-to-cart-text--added'
        );

      const addedText =
        addedTextElement?.textContent?.trim() ||
        Theme.translations.added;

      this.#setLiveRegionText(
        addedText
      );

      setTimeout(() => {
        this.#clearLiveRegionText();
      }, SUCCESS_MESSAGE_DISPLAY_DURATION);
    }
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (!quantityLabel) {
      return;
    }

    const inCartText =
      quantityLabel.textContent?.match(
        /\((\d+)\s+(.+)\)/
      );

    if (
      inCartText &&
      inCartText[2]
    ) {
      quantityLabel.textContent =
        `(${cartQty} ${inCartText[2]})`;
    }

    quantityLabel.classList.toggle(
      'hidden',
      cartQty === 0
    );
  }

  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent =
      text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent =
      '';
  }

  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );
    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();
    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */ (
          newElement.cloneNode(true)
        )
      );
    }
  }

  /**
   * Variant selection.
   *
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (
    event
  ) => {
    const sourceCard =
      /** @type {Element | null} */ (
        event.target
      )?.closest(
        'product-card'
      );

    if (
      sourceCard &&
      !sourceCard.contains(this)
    ) {
      return;
    }

    const generation =
      ++this.#variantChangeGeneration;

    this.#variantChangeInProgress =
      true;

    try {
      const { detail } =
        await event.promise;

      if (!detail?.html) {
        return;
      }

      const {
        html,
        newProduct,
        productId,
        resource,
      } = detail;

      if (newProduct) {
        this.dataset.productId =
          newProduct.id;
      } else if (
        productId &&
        productId !==
          this.dataset.productId
      ) {
        return;
      }

      const { variantId } =
        this.refs;

      variantId.value =
        resource?.id ?? '';

      const {
        addToCartButtonContainer:
          currentAddToCartButtonContainer,
        acceleratedCheckoutButtonContainer,
      } = this.refs;

      const currentAddToCartButton =
        currentAddToCartButtonContainer
          ?.refs.addToCartButton;

      if (
        !currentAddToCartButtonContainer ||
        (!currentAddToCartButton &&
          !acceleratedCheckoutButtonContainer)
      ) {
        return;
      }

      if (
        resource == null ||
        resource.available === false
      ) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton =
        html.querySelector(
          'product-form-component [ref="addToCartButton"]'
        );

      if (
        newAddToCartButton &&
        currentAddToCartButton
      ) {
        morph(
          currentAddToCartButton,
          newAddToCartButton
        );
      }

      if (
        acceleratedCheckoutButtonContainer
      ) {
        if (
          resource == null ||
          resource.available === false
        ) {
          acceleratedCheckoutButtonContainer.setAttribute(
            'hidden',
            'true'
          );
        } else {
          acceleratedCheckoutButtonContainer.removeAttribute(
            'hidden'
          );
        }
      }

      if (resource) {
        const productVariantMedia =
          resource.featured_media
            ?.preview_image?.src;

        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            `${productVariantMedia}&width=100`
          );
        }
      }

      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      const newQuantityInput =
        /** @type {HTMLInputElement | null} */ (
          html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );

      if (
        quantitySelector?.updateConstraints &&
        newQuantityInput
      ) {
        quantitySelector.updateConstraints(
          newQuantityInput.min,
          newQuantityInput.max ||
            null,
          newQuantityInput.step
        );

        this.dataset.quantityDefault =
          newQuantityInput.min ||
          '1';
      }

      const newQuantityRules =
        html.querySelector(
          '.quantity-rules'
        );

      const isQuantityRulesChanging =
        !!quantityRules !==
        !!newQuantityRules;

      const newPricePerItem =
        html.querySelector(
          'price-per-item'
        );

      const isPricePerItemChanging =
        !!pricePerItem !==
        !!newPricePerItem;

      if (
        (isQuantityRulesChanging ||
          isPricePerItemChanging) &&
        quantitySelector
      ) {
        const currentQuantityValue =
          quantitySelector.getValue?.();

        const newProductFormButtons =
          html.querySelector(
            '.product-form-buttons'
          );

        if (
          productFormButtons &&
          newProductFormButtons
        ) {
          morph(
            productFormButtons,
            newProductFormButtons
          );

          const newQuantityInputElement =
            /** @type {HTMLInputElement | null} */ (
              html.querySelector(
                'quantity-selector-component input[ref="quantityInput"]'
              )
            );

          if (
            this.refs.quantitySelector
              ?.updateConstraints &&
            newQuantityInputElement &&
            currentQuantityValue
          ) {
            this.refs.quantitySelector.setValue(
              currentQuantityValue
            );

            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max ||
                null,
              newQuantityInputElement.step
            );

            this.dataset.quantityDefault =
              newQuantityInputElement.min ||
              '1';
          }
        }
      } else {
        const morphTargets = [
          [
            '.quantity-label',
            quantityLabel,
            quantitySelector,
          ],
          [
            '.quantity-rules',
            quantityRules,
            this.refs.productFormButtons,
          ],
          [
            'price-per-item',
            pricePerItem,
            quantitySelectorWrapper,
          ],
        ];

        for (
          const [
            selector,
            currentElement,
            fallback,
          ] of morphTargets
        ) {
          this.#morphOrUpdateElement(
            currentElement,
            html.querySelector(
              selector
            ),
            fallback
          );
        }
      }

      const currentVolumePricing =
        this.refs.volumePricing;

      const newVolumePricing =
        html.querySelector(
          'volume-pricing'
        );

      this.#morphOrUpdateElement(
        currentVolumePricing,
        newVolumePricing,
        this.refs.productFormButtons
      );

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) {
        return;
      }

      this.#refreshCart().then(
        (cart) =>
          this.#updateCartQuantity(
            cart
          )
      );
    } finally {
      if (
        generation ===
        this.#variantChangeGeneration
      ) {
        this.#variantChangeInProgress =
          false;

        if (
          this.#addToCartQueue.length >
          0
        ) {
          const queuedItems = [
            ...this.#addToCartQueue,
          ];

          this.#addToCartQueue = [];

          this.#processBatchAddToCart(
            queuedItems
          );
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}